import { RUNTIME_BACKEND_URL } from '../../lib/backendUrl';
import { useEffect, useState, useMemo } from 'react';
import { useHesapAuth } from '../../store/hesapStore';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Card, CardContent } from '../../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { toast } from 'sonner';
import { Plus, Minus, ShoppingCart, Send, Loader2, X, Printer, CreditCard } from 'lucide-react';
import { useReceiptPrinter } from '../../components/ReceiptPrinter';

const API = `${RUNTIME_BACKEND_URL}/api/hesap`;

function formatPrice(val) {
  return Number(val || 0).toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

export default function HesapYemekSiparisPage() {
  const { token } = useHesapAuth();
  const headers = { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' };

  const [menuItems, setMenuItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [cart, setCart] = useState([]);
  const [tableNo, setTableNo] = useState('');
  const [note, setNote] = useState('');
  const [sending, setSending] = useState(false);
  const [filterCat, setFilterCat] = useState('');
  const [showCart, setShowCart] = useState(false);
  const [variantDialog, setVariantDialog] = useState(null); // menu item for variant selection
  const [lastOrder, setLastOrder] = useState(null);
  const { printReceipt } = useReceiptPrinter();

  useEffect(() => { loadMenu(); }, []);

  const loadMenu = async () => {
    try {
      const res = await fetch(`${API}/menu-items`, { headers });
      if (res.ok) setMenuItems((await res.json()).filter(i => i.is_active));
    } finally { setLoading(false); }
  };

  const categories = useMemo(() => [...new Set(menuItems.map(i => i.category).filter(Boolean))], [menuItems]);
  const filtered = filterCat ? menuItems.filter(i => i.category === filterCat) : menuItems;

  const addToCart = (item) => {
    if (item.variants?.length > 0) {
      setVariantDialog(item);
      return;
    }
    setCart(prev => {
      const existing = prev.find(c => c.menu_item_id === item.id && !c.variant);
      if (existing) return prev.map(c => c.menu_item_id === item.id && !c.variant ? { ...c, quantity: c.quantity + 1 } : c);
      return [...prev, { menu_item_id: item.id, name: item.name, price: item.price, quantity: 1 }];
    });
  };

  const addVariantItem = (variant) => {
    const item = variantDialog;
    const label = variant.label;
    setCart(prev => {
      const key = `${item.id}_${label}`;
      const existing = prev.find(c => c.menu_item_id === item.id && c.variant === label);
      if (existing) return prev.map(c => c.menu_item_id === item.id && c.variant === label ? { ...c, quantity: c.quantity + 1 } : c);
      return [...prev, {
        menu_item_id: item.id, name: item.name, price: variant.price,
        quantity: 1, variant: label
      }];
    });
    setVariantDialog(null);
    toast.success(`${item.name} (${label}) eklendi`);
  };

  const updateQty = (idx, delta) => {
    setCart(prev => prev.map((c, i) => {
      if (i === idx) { const nq = c.quantity + delta; return nq > 0 ? { ...c, quantity: nq } : c; }
      return c;
    }).filter(c => c.quantity > 0));
  };

  const removeFromCart = (idx) => setCart(prev => prev.filter((_, i) => i !== idx));

  const total = cart.reduce((s, c) => s + c.price * c.quantity, 0);
  const totalItems = cart.reduce((s, c) => s + c.quantity, 0);

  // Ödeme state'leri
  const [paymentStep, setPaymentStep] = useState(false);
  const [payMethod, setPayMethod] = useState('nakit');
  const [givenAmount, setGivenAmount] = useState('');
  const changeAmount = payMethod === 'nakit' && givenAmount ? Math.max(0, parseFloat(givenAmount) - total) : 0;

  const openPayment = () => {
    if (cart.length === 0) { toast.error('Sepet boş'); return; }
    setPaymentStep(true);
    setPayMethod('nakit');
    setGivenAmount('');
  };

  const submitOrder = async () => {
    if (payMethod === 'nakit' && givenAmount && parseFloat(givenAmount) < total) {
      toast.error('Verilen tutar yetersiz');
      return;
    }
    setSending(true);
    try {
      const res = await fetch(`${API}/food/orders`, {
        method: 'POST', headers,
        body: JSON.stringify({
          items: cart, table_no: tableNo, note,
          payment_method: payMethod,
          paid_amount: payMethod === 'nakit' ? parseFloat(givenAmount || total) : total,
          change_amount: changeAmount
        })
      });
      if (!res.ok) throw new Error();
      const orderData = await res.json();
      setLastOrder(orderData);
      toast.success(`Sipariş gönderildi! Toplam: ${total.toLocaleString('tr-TR')} TL`);
      setCart([]); setTableNo(''); setNote(''); setShowCart(false); setPaymentStep(false);
    } catch { toast.error('Sipariş gönderilemedi'); }
    finally { setSending(false); }
  };

  if (loading) return <div className="flex justify-center py-12"><Loader2 className="h-8 w-8 animate-spin" /></div>;

  return (
    <div className="space-y-4" data-testid="hesap-yemek-siparis">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-slate-800">{`Yemek Sipariş`}</h1>
        <Button onClick={() => setShowCart(true)} className="relative bg-emerald-600 hover:bg-emerald-700" data-testid="cart-btn">
          <ShoppingCart className="h-4 w-4 mr-2" />
          Sepet ({totalItems})
          {totalItems > 0 && <span className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full w-5 h-5 text-xs flex items-center justify-center">{totalItems}</span>}
        </Button>
      </div>

      {categories.length > 0 && (
        <div className="flex gap-2 flex-wrap">
          <button onClick={() => setFilterCat('')}
            className={`px-4 py-2 rounded-full text-sm font-medium transition ${!filterCat ? 'bg-emerald-600 text-white' : 'bg-slate-100 hover:bg-slate-200'}`}>
            {`Tümü`}
          </button>
          {categories.map(c => (
            <button key={c} onClick={() => setFilterCat(c)}
              className={`px-4 py-2 rounded-full text-sm font-medium transition ${filterCat === c ? 'bg-emerald-600 text-white' : 'bg-slate-100 hover:bg-slate-200'}`}>{c}</button>
          ))}
        </div>
      )}

      {filtered.length === 0 ? (
        <div className="text-center py-12 text-slate-400">
          <p className="text-lg">{`Henüz menü ögesi eklenmemiş`}</p>
          <p className="text-sm mt-1">{`Menü Yönetimi sayfasından ürün ekleyin`}</p>
        </div>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3">
          {filtered.map(item => {
            const inCart = cart.filter(c => c.menu_item_id === item.id);
            const cartQty = inCart.reduce((s, c) => s + c.quantity, 0);
            return (
              <Card key={item.id} className={`overflow-hidden cursor-pointer transition hover:shadow-lg ${cartQty > 0 ? 'ring-2 ring-emerald-500' : ''}`}
                onClick={() => addToCart(item)} data-testid={`menu-item-${item.id}`}>
                <div className="aspect-square bg-slate-100 relative overflow-hidden">
                  {item.image ? (
                    <img src={item.image} alt={item.name} className="w-full h-full object-cover" />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center text-slate-300 text-4xl font-bold">{item.name.charAt(0)}</div>
                  )}
                  {cartQty > 0 && <div className="absolute top-2 right-2 bg-emerald-600 text-white rounded-full w-7 h-7 flex items-center justify-center text-sm font-bold shadow">{cartQty}</div>}
                  {item.variants?.length > 0 && (
                    <div className="absolute bottom-2 left-2 bg-orange-500 text-white rounded px-1.5 py-0.5 text-[10px] font-medium">
                      {item.variants.length} seçenek
                    </div>
                  )}
                </div>
                <CardContent className="p-3">
                  <p className="font-medium text-sm truncate">{item.name}</p>
                  <p className="text-emerald-600 font-bold">{item.variants?.length > 0 ? `${item.price.toLocaleString('tr-TR')} TL'den` : `${item.price.toLocaleString('tr-TR')} TL`}</p>
                  {item.variants?.length > 0 && <p className="text-[10px] text-orange-500 truncate">{item.variants.map(v => v.label).join(' / ')}</p>}
                  {item.category && <p className="text-[10px] text-slate-400">{item.category}</p>}
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {/* Seçenek Dialog */}
      <Dialog open={!!variantDialog} onOpenChange={() => setVariantDialog(null)}>
        <DialogContent className="max-w-xs">
          <DialogHeader><DialogTitle>{variantDialog?.name}</DialogTitle></DialogHeader>
          <div className="space-y-2">
            <p className="text-sm text-slate-500">Seçenek seçin:</p>
            {(variantDialog?.variants || []).map((v, i) => (
              <button key={i} onClick={() => addVariantItem(v)}
                className="w-full flex items-center justify-between p-3 bg-slate-50 hover:bg-emerald-50 rounded-lg border hover:border-emerald-300 transition"
                data-testid={`variant-option-${i}`}>
                <span className="font-medium">{v.label}</span>
                <span className="font-bold text-emerald-700">{Number(v.price).toLocaleString('tr-TR')} TL</span>
              </button>
            ))}
            <Button variant="outline" onClick={() => setVariantDialog(null)} className="w-full mt-2">İptal</Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Sepet Dialog */}
      <Dialog open={showCart} onOpenChange={setShowCart}>
        <DialogContent className="max-w-md max-h-[80vh] overflow-y-auto">
          <DialogHeader><DialogTitle>{`Sipariş Sepeti`}</DialogTitle></DialogHeader>
          {cart.length === 0 ? (
            <p className="text-center text-slate-400 py-6">{`Sepet boş`}</p>
          ) : (
            <div className="space-y-3">
              {cart.map((item, idx) => (
                <div key={idx} className="flex items-center justify-between p-2 bg-slate-50 rounded-lg">
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium truncate">{item.name}{item.variant ? ` (${item.variant})` : ''}</p>
                    <p className="text-xs text-slate-400">{item.price.toLocaleString('tr-TR')} TL</p>
                  </div>
                  <div className="flex items-center gap-2">
                    <Button variant="outline" size="icon" className="h-7 w-7" onClick={() => updateQty(idx, -1)}><Minus className="h-3 w-3" /></Button>
                    <span className="w-6 text-center font-bold text-sm">{item.quantity}</span>
                    <Button variant="outline" size="icon" className="h-7 w-7" onClick={() => updateQty(idx, 1)}><Plus className="h-3 w-3" /></Button>
                    <span className="w-16 text-right font-medium text-sm">{(item.price * item.quantity).toLocaleString('tr-TR')} TL</span>
                    <Button variant="ghost" size="icon" className="h-7 w-7 text-red-400" onClick={() => removeFromCart(idx)}><X className="h-3.5 w-3.5" /></Button>
                  </div>
                </div>
              ))}
              <div className="border-t pt-3 space-y-2">
                <div className="grid grid-cols-2 gap-2">
                  <div className="space-y-1"><Label className="text-xs">Masa No</Label><Input value={tableNo} onChange={e => setTableNo(e.target.value)} placeholder="Masa" /></div>
                  <div className="space-y-1"><Label className="text-xs">Not</Label><Input value={note} onChange={e => setNote(e.target.value)} placeholder={`Özel istek...`} /></div>
                </div>
                <div className="flex items-center justify-between p-3 bg-emerald-50 rounded-lg">
                  <span className="font-medium">Toplam:</span>
                  <span className="text-xl font-bold text-emerald-700">{total.toLocaleString('tr-TR')} TL</span>
                </div>
                
                {!paymentStep ? (
                  <Button onClick={openPayment} className="w-full bg-emerald-600 hover:bg-emerald-700" data-testid="proceed-to-payment-btn">
                    <CreditCard className="h-4 w-4 mr-2" />Ödeme Yap
                  </Button>
                ) : (
                  <div className="space-y-3 p-3 bg-slate-50 rounded-lg border">
                    <p className="text-sm font-medium text-slate-700">Ödeme Yöntemi</p>
                    <div className="flex gap-2">
                      <Button variant={payMethod === 'nakit' ? 'default' : 'outline'} size="sm" className={payMethod === 'nakit' ? 'flex-1 bg-green-600 hover:bg-green-700' : 'flex-1'}
                        onClick={() => { setPayMethod('nakit'); setGivenAmount(''); }} data-testid="pay-nakit-btn">
                        Nakit
                      </Button>
                      <Button variant={payMethod === 'kart' ? 'default' : 'outline'} size="sm" className={payMethod === 'kart' ? 'flex-1 bg-blue-600 hover:bg-blue-700' : 'flex-1'}
                        onClick={() => setPayMethod('kart')} data-testid="pay-kart-btn">
                        Kart
                      </Button>
                    </div>
                    
                    {payMethod === 'nakit' && (
                      <div className="space-y-2">
                        <div className="space-y-1">
                          <Label className="text-xs">Verilen Tutar (TL)</Label>
                          <Input type="number" value={givenAmount} onChange={e => setGivenAmount(e.target.value)}
                            placeholder={total.toLocaleString('tr-TR')} autoFocus data-testid="given-amount-input" />
                        </div>
                        {givenAmount && parseFloat(givenAmount) >= total && (
                          <div className="p-2 bg-green-50 rounded-lg text-center border border-green-200">
                            <p className="text-xs text-green-700">Para Üstü</p>
                            <p className="text-lg font-bold text-green-700">{changeAmount.toLocaleString('tr-TR', { minimumFractionDigits: 2 })} TL</p>
                          </div>
                        )}
                        {givenAmount && parseFloat(givenAmount) < total && (
                          <p className="text-xs text-red-500 text-center">Tutar yetersiz</p>
                        )}
                      </div>
                    )}
                    
                    <Button onClick={submitOrder} disabled={sending || (payMethod === 'nakit' && givenAmount && parseFloat(givenAmount) < total)}
                      className="w-full bg-emerald-600 hover:bg-emerald-700" data-testid="submit-order-btn">
                      {sending ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Send className="h-4 w-4 mr-2" />}
                      Siparişi Onayla
                    </Button>
                  </div>
                )}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Son Sipariş Fiş Dialog */}
      <Dialog open={!!lastOrder} onOpenChange={() => setLastOrder(null)}>
        <DialogContent className="max-w-xs">
          <DialogHeader><DialogTitle>Sipariş Oluşturuldu</DialogTitle></DialogHeader>
          <div className="space-y-3 text-center">
            <div className="p-4 bg-emerald-50 rounded-lg">
              <p className="text-sm text-slate-500">Toplam</p>
              <p className="text-2xl font-bold text-emerald-700">{formatPrice(lastOrder?.total)} TL</p>
              {lastOrder?.table_no && <p className="text-sm text-slate-600 mt-1">Masa: {lastOrder.table_no}</p>}
            </div>
            <div className="flex gap-2">
              <Button variant="outline" onClick={() => setLastOrder(null)} className="flex-1">Kapat</Button>
              <Button onClick={() => { printReceipt(lastOrder); }} className="flex-1 bg-blue-600 hover:bg-blue-700" data-testid="print-receipt-btn">
                <Printer className="h-4 w-4 mr-1" />Fiş Yazdır
              </Button>
              <Button onClick={() => { printReceipt(lastOrder, { showKitchen: true }); }} className="flex-1 bg-orange-600 hover:bg-orange-700" data-testid="print-kitchen-btn">
                <Printer className="h-4 w-4 mr-1" />Mutfak
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
